import { useCallback, useEffect, useRef, useState } from 'react';
import { POS_DRAG_THRESHOLD_PX, POS_DRAG_LONG_PRESS_MS } from '../constants/posConst';

/** Elements inside a card that keep their own pointer behaviour. */
const NO_DRAG_SELECTOR = 'input, textarea, select, a, [data-no-drag]';

/**
 * Drag-to-reorder for a grid of cards, with mouse, pen and touch.
 *
 * WHY NOT HTML5 DRAG AND DROP. Its events do not fire for touch on most
 * tablets, and a till is a tablet more often than not. This uses pointer
 * events and finds the card under the pointer with elementFromPoint, so the
 * same code serves a mouse on a desk and a finger at the counter.
 *
 * TOUCH NEEDS A LONG PRESS. A finger that moves straight away is scrolling the
 * page; one that rests on a card for POS_DRAG_LONG_PRESS_MS is picking it up.
 * Once a drag has started, touchmove is cancelled so the page stops scrolling
 * under it. A mouse starts dragging after POS_DRAG_THRESHOLD_PX of movement.
 *
 * THE LIST REORDERS LIVE: every time the pointer crosses another card,
 * `onMove(fromId, toId)` is called and the caller moves the item — the card
 * travels with the pointer through the grid rather than a ghost floating over
 * it. A click that ends a drag is swallowed, so dropping a till card does not
 * also open it.
 *
 * @param {object} params
 * @param {boolean} params.enabled - false turns every handler into a no-op
 * @param {(fromId: string, toId: string) => void} params.onMove
 * @returns {{draggingId: string|null, getDragProps: (id: string) => object}}
 *   spread getDragProps(id) onto each card
 */
function useDragReorder({ enabled, onMove }) {
  const [draggingId, setDraggingId] = useState(null);
  // Mutable drag state — read inside window listeners, so it cannot be React
  // state without going stale between renders.
  const drag = useRef(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  /**
   * Swallows the one click the browser fires after a drag is released.
   * @returns {void}
   */
  const suppressNextClick = () => {
    const swallow = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    window.addEventListener('click', swallow, { capture: true, once: true });
    // If no click follows (released off the card), stop listening anyway.
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
  };

  /**
   * Tears down the listeners and timer of the current press, if any.
   * @returns {void}
   */
  const endPress = useCallback(() => {
    const current = drag.current;
    if (!current) return;
    clearTimeout(current.timer);
    window.removeEventListener('pointermove', current.handleMove);
    window.removeEventListener('pointerup', current.handleUp);
    window.removeEventListener('pointercancel', current.handleCancel);
    window.removeEventListener('touchmove', current.blockScroll);
    drag.current = null;
    setDraggingId(null);
  }, []);

  // A drag in progress when the grid unmounts or custom mode is switched off.
  useEffect(() => endPress, [endPress]);
  useEffect(() => { if (!enabled) endPress(); }, [enabled, endPress]);

  /**
   * Starts tracking a press on one card.
   *
   * @param {PointerEvent} event
   * @param {string} id - the card's item id
   * @returns {void}
   */
  const handlePointerDown = (event, id) => {
    if (!enabled || drag.current) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest(NO_DRAG_SELECTOR)) return;

    const isTouch = event.pointerType !== 'mouse';
    const state = {
      id,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      timer: null,
    };

    const activate = () => {
      state.active = true;
      setDraggingId(id);
    };

    state.blockScroll = (touchEvent) => {
      if (state.active) touchEvent.preventDefault();
    };

    state.handleMove = (moveEvent) => {
      const dx = moveEvent.clientX - state.startX;
      const dy = moveEvent.clientY - state.startY;
      const distance = Math.hypot(dx, dy);

      if (!state.active) {
        // A finger that moves before the long press completes is scrolling.
        if (isTouch) {
          if (distance > POS_DRAG_THRESHOLD_PX) endPress();
          return;
        }
        if (distance < POS_DRAG_THRESHOLD_PX) return;
        activate();
      }

      moveEvent.preventDefault();
      const target = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY)
        ?.closest('[data-drag-id]');
      const toId = target?.getAttribute('data-drag-id');
      if (toId && toId !== state.id) onMoveRef.current(state.id, toId);
    };

    state.handleUp = () => {
      const wasActive = state.active;
      endPress();
      if (wasActive) suppressNextClick();
    };

    state.handleCancel = () => endPress();

    drag.current = state;
    window.addEventListener('pointermove', state.handleMove);
    window.addEventListener('pointerup', state.handleUp);
    window.addEventListener('pointercancel', state.handleCancel);
    // Non-passive, so preventDefault can stop the page scrolling mid-drag.
    window.addEventListener('touchmove', state.blockScroll, { passive: false });
    if (isTouch) state.timer = setTimeout(activate, POS_DRAG_LONG_PRESS_MS);
  };

  /**
   * The props one draggable card needs.
   *
   * @param {string} id - the item's id
   * @returns {object} data attribute, pointer handler and a context-menu guard
   */
  const getDragProps = (id) => (enabled ? {
    'data-drag-id': id,
    onPointerDown: (event) => handlePointerDown(event, id),
    // A long press on touch otherwise opens the image/share menu.
    onContextMenu: (event) => event.preventDefault(),
  } : {});

  return { draggingId, getDragProps };
}

export default useDragReorder;
